import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #marqueeFrame = undefined;
  #marqueeOffset = 0;
  #marqueeStep = 0.35;
  #marqueeGap = 48;
  #handleResize = () => this.#setupMarquee();

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    if ((this.refs.slides ?? []).length === 1) {
      this.#setupMarquee();
      window.addEventListener('resize', this.#handleResize);
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    this.#teardownMarquee();
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #setupMarquee() {
    const [slide] = this.refs.slides ?? [];
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    const text = slide?.querySelector('.announcement-bar__text');

    if (!slide || !slidesContainer || !text) return;

    this.#teardownMarquee();

    const message = text.textContent?.trim();
    if (!message) return;

    this.setAttribute('data-marquee', 'true');
    slidesContainer.innerHTML = '';

    const track = document.createElement('div');
    track.className = 'announcement-bar__marquee-track';

    const createItem = () => {
      const item = document.createElement('span');
      item.className = 'announcement-bar__marquee-item';
      item.textContent = message;
      return item;
    };

    track.appendChild(createItem());
    slidesContainer.appendChild(track);

    while (track.scrollWidth < window.innerWidth * 3) {
      track.appendChild(createItem());
    }

    this.#marqueeOffset = window.innerWidth;
    track.style.transform = `translateX(${this.#marqueeOffset}px)`;
    this.#marqueeFrame = window.requestAnimationFrame(() => this.#runMarquee(track));
  }

  #runMarquee(track) {
    const firstItem = track.firstElementChild;

    if (!(firstItem instanceof HTMLElement)) return;

    this.#marqueeOffset -= this.#marqueeStep;
    track.style.transform = `translateX(${this.#marqueeOffset}px)`;

    const firstItemWidth = firstItem.offsetWidth + this.#marqueeGap;

    if (this.#marqueeOffset <= -firstItemWidth) {
      this.#marqueeOffset += firstItemWidth;
      track.appendChild(firstItem);
      track.style.transform = `translateX(${this.#marqueeOffset}px)`;
    }

    this.#marqueeFrame = window.requestAnimationFrame(() => this.#runMarquee(track));
  }

  #teardownMarquee() {
    if (this.#marqueeFrame) {
      window.cancelAnimationFrame(this.#marqueeFrame);
      this.#marqueeFrame = undefined;
    }
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
